import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { HERO_SLIDES, STATS } from "../lib/siteData";
import Counter from "./Counter";

export default function Hero() {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const t = setInterval(() => setIndex((i) => (i + 1) % HERO_SLIDES.length), 6000);
    return () => clearInterval(t);
  }, []);

  return (
    <section
      id="home"
      className="relative -mt-[120px] lg:-mt-[152px] min-h-[100svh] w-full overflow-hidden bg-[#0A192F] text-white"
      data-testid="hero-section"
    >
      {/* Slideshow */}
      <div className="absolute inset-0">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={s.url}
            className={`absolute inset-0 transition-opacity duration-[1400ms] ${i === index ? "opacity-100" : "opacity-0"}`}
            aria-hidden={i !== index}
          >
            <img
              src={s.url}
              alt={s.label}
              className={`w-full h-full object-cover ${i === index ? "kenburns" : ""}`}
              loading={i === 0 ? "eager" : "lazy"}
            />
          </div>
        ))}
        <div className="absolute inset-0 hero-overlay" />
        <div className="absolute inset-0 blueprint-grid-dark opacity-60 pointer-events-none" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 pt-36 lg:pt-44 pb-16 flex flex-col min-h-[100svh]">
        <div className="flex-1 flex flex-col justify-center max-w-4xl">
          <div className="flex items-center gap-3 mb-6" data-testid="hero-overline">
            <span className="h-px w-12 bg-[#F5A400]" />
            <span className="text-[11px] sm:text-xs tracking-[0.35em] font-semibold text-[#F5A400]">
              JAMAICA · CARIBBEAN · UNITED KINGDOM
            </span>
          </div>

          <h1 className="font-display font-bold text-white text-[42px] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[88px] tracking-tight">
            CONCEPT<br />
            TO <span className="text-[#F5A400]">COMPLETION.</span>
          </h1>

          <p className="mt-6 text-base sm:text-lg lg:text-xl text-white/80 max-w-2xl leading-relaxed">
            Engineering, Architecture & Construction solutions built for Jamaica and beyond.
            One partner from land acquisition to final handover — investor-ready,
            regulator-compliant, and delivered with precision.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4" data-testid="hero-cta-group">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-2 bg-[#F5A400] text-[#0A192F] hover:bg-[#D99000] font-bold px-8 py-4 transition-all duration-300 hover:-translate-y-0.5"
              data-testid="hero-cta-start-project"
            >
              Start Your Project
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-md border border-white/30 text-white hover:bg-white hover:text-[#0A192F] font-bold px-8 py-4 transition-all duration-300"
              data-testid="hero-cta-consultation"
            >
              Request Consultation
            </Link>
          </div>

          {/* Current slide label */}
          <div className="hidden lg:flex absolute right-8 bottom-40 items-center gap-3 text-white/70 text-[11px] tracking-[0.3em] font-semibold" data-testid="hero-slide-label">
            <span className="h-px w-10 bg-white/40" />
            {HERO_SLIDES[index].label}
          </div>
        </div>

        {/* Stats bar */}
        <div className="mt-12 lg:mt-20 border-t border-white/15 pt-8 lg:pt-10 grid grid-cols-1 sm:grid-cols-3 gap-6 lg:gap-10" data-testid="hero-stats">
          {STATS.map((s) => (
            <div key={s.label} className="flex flex-col" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <div className="font-display font-bold text-4xl sm:text-5xl text-white tracking-tight">
                <Counter value={s.value} suffix={s.suffix} />
              </div>
              <div className="mt-1 text-xs sm:text-sm text-white/70 tracking-[0.18em] uppercase font-semibold">
                {s.label}
              </div>
            </div>
          ))}
        </div>

        {/* Slide indicators */}
        <div className="mt-8 flex items-center gap-2" data-testid="hero-slide-indicators">
          {HERO_SLIDES.map((_, i) => (
            <button
              key={i}
              onClick={() => setIndex(i)}
              className={`h-1 transition-all duration-500 ${i === index ? "w-14 bg-[#F5A400]" : "w-8 bg-white/30 hover:bg-white/60"}`}
              aria-label={`Go to slide ${i + 1}`}
              data-testid={`hero-slide-indicator-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
